import type { ProjectionResult } from "@/lib/retirement-calc";

interface Props {
  result: ProjectionResult;
  fmtCompact: Intl.NumberFormat;
  fmtFull: Intl.NumberFormat;
  inflationPct: number;
  shariahMode: boolean;
  showAfterTax?: boolean;
  taxRatePct?: number;
}


const Stat = ({
  label,
  compact,
  full,
  accent,
  sign,
}: {
  label: string;
  compact: string;
  full: string;
  accent?: "primary" | "destructive";
  sign?: string;
}) => (
  <div
    className={`rounded-2xl border p-4 sm:p-5 shadow-sm bg-card ${
      accent === "primary"
        ? "border-primary/30"
        : accent === "destructive"
          ? "border-destructive/30"
          : "border-border"
    }`}
  >
    <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-muted-foreground font-semibold mb-1.5">
      {label}
    </div>
    <div
      className={`text-2xl sm:text-3xl font-extrabold tabular-nums leading-tight ${
        accent === "primary"
          ? "text-primary"
          : accent === "destructive"
            ? "text-destructive"
            : "text-foreground"
      }`}
    >
      {sign}
      {compact}
    </div>
    <div className="text-[11px] text-muted-foreground tabular-nums mt-0.5 truncate">
      {sign}
      {full}
    </div>
  </div>
);

const Summary = ({
  result,
  fmtCompact,
  fmtFull,
  inflationPct,
  shariahMode,
  showAfterTax = true,
  taxRatePct = 0,
}: Props) => {
  const projected = result.projectedAtRetirementReal;
  const target = result.nestEggTargetToday;
  const gap = projected - target;
  const onTrack = gap >= 0;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Stat
          label="Projected"
          compact={fmtCompact.format(projected)}
          full={fmtFull.format(projected)}
          accent="primary"
        />
        <Stat
          label={showAfterTax ? "Target (25×)" : `Target (25× ÷ ${(100 - taxRatePct).toFixed(0)}%)`}
          compact={fmtCompact.format(target)}
          full={fmtFull.format(target)}
        />
        <Stat
          label={onTrack ? "Surplus" : "Shortfall"}
          compact={fmtCompact.format(Math.abs(gap))}
          full={fmtFull.format(Math.abs(gap))}
          sign={gap >= 0 ? "+" : "−"}
          accent={onTrack ? "primary" : "destructive"}
        />
      </div>

      <p className="text-xs text-muted-foreground px-1">
        Shown in today's money, adjusted for {inflationPct.toFixed(1)}% inflation
        {showAfterTax ? (
          <> · after-tax target</>
        ) : (
          <> · before-tax target (grossed up at {taxRatePct.toFixed(1)}% withdrawal tax)</>
        )}
        {shariahMode && <> · includes ~2.58% annual Zakat above Nisab</>}.
      </p>
    </div>
  );
};

export default Summary;

